import { TAuthorNormalisedContent } from '@/entities/Content/ui/Content.types';
import * as S from './AuthorPage.styles';
import { AuthorsLinks } from './AuthorsLinks';
import Layout from '@/shared/Layout/ui/Layout';
import { myImageLoader } from '@/lib/imageLoader';
import { TopAuthorsBlogs } from './TopAuthorsBlogs';
import { IBreadcrumbsItem } from '@/shared/Breadcrumbs/domain/Breadcrumbs.domain';
import { Breadcrumbs } from '@/shared/Breadcrumbs/ui';
import { Container } from '@/common/ui/containers/Container';
import { TPublicBlogs } from '@/entities/Blogs/domain/Blogs.domain';
import { PublicationsGrid } from './AuthorsPublications';
import { cssVars } from '@/styles/Themes/cssVars';
import { VisitorsGuideBlogs } from '../DestinationsPage/ui/VisitorsGuide/VisitorsGuideBlogs';
import { useMedia } from '@/common/service/hooks/useMedia';
import { IMeta } from '@/common/domain/Meta.domain';
import { CommonMeta } from '@/common/ui/meta/CommonMeta';
import { MartkdownHtml } from '@/shared/MarkdownHtml';

const AuthorPage = ({
    meta,
    structuredData,
    pageContent,
    mainImage,
    breadcrumbs,
    topBlogs,
    allBlogs,
}: {
    meta: IMeta;
    structuredData: string;
    pageContent: TAuthorNormalisedContent;
    mainImage: string;
    breadcrumbs: IBreadcrumbsItem[];
    allBlogs: TPublicBlogs;
    topBlogs: TPublicBlogs;
}) => {
    const isMobileView = useMedia('(max-width: 991px)');

    return (
        <Layout>
            <CommonMeta
                title={meta.title}
                description={meta.description}
                imageSrc={mainImage}
                slug={`/blog/authors/${pageContent.slug}/`}
                canonical={meta.canonical}
                structuredData={[structuredData]}
            />
            <div style={{ marginTop: '-10px' }}>
                <S.MainWrapper>
                    <Breadcrumbs
                        breadcrumbs={breadcrumbs}
                        backgroundColor={cssVars.bg_lightBlue_darker}
                    />
                    <Container>
                        <S.MainContent>
                            <S.AuthorInfo>
                                <h1>{pageContent.title}</h1>
                                <h2>{pageContent.job_title}</h2>
                                <S.Bio>
                                    <MartkdownHtml content={pageContent.description} />
                                </S.Bio>
                                <S.Education>
                                    <label>Education:</label>
                                    <span>{pageContent.education}</span>
                                </S.Education>
                                <AuthorsLinks links={pageContent.links} />
                            </S.AuthorInfo>
                            {Boolean(mainImage) && (
                                <S.ProfileImage>
                                    <S.MainImage
                                        loader={myImageLoader}
                                        src={mainImage}
                                        width={400}
                                        height={400}
                                        alt="Profile photo"
                                    />
                                </S.ProfileImage>
                            )}
                        </S.MainContent>
                    </Container>
                    <S.Gradient />
                </S.MainWrapper>
                {!!pageContent.video && (
                    <Container>
                        <S.AboutSection>
                            <S.Title>Featured Video</S.Title>
                            {Boolean(pageContent.video) && (
                                <S.VideoContainer>
                                    <S.Video
                                        src={`https://www.youtube.com/embed/${pageContent.video}`}
                                        title="Is Hilton Head SC Worth Visiting?"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    />
                                </S.VideoContainer>
                            )}

                            <S.ContentCard
                                dangerouslySetInnerHTML={{
                                    __html: pageContent.content,
                                }}
                            />
                        </S.AboutSection>
                    </Container>
                )}
                {!!pageContent.additional_videos.length && (
                    <S.GallerySection>
                        <Container>
                            <S.VideoTitle>
                                More Videos From {pageContent.title.split(' ')[0]}
                            </S.VideoTitle>
                            <S.VideoGrid>
                                {pageContent.additional_videos.map((video, idx) => (
                                    <S.VideoWrapper key={idx}>
                                        <S.Video
                                            src={`https://www.youtube.com/embed/${video}`}
                                            //title={video.title}
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                        />
                                    </S.VideoWrapper>
                                ))}
                            </S.VideoGrid>
                        </Container>
                    </S.GallerySection>
                )}
                <Container>
                    {!!topBlogs.items.length && (
                        <TopAuthorsBlogs
                            authorImage={mainImage}
                            blogs={topBlogs}
                            title={pageContent.title}
                        />
                    )}
                    {!!pageContent.publications.length && (
                        <PublicationsGrid
                            publications={pageContent.publications || []}
                            title={pageContent.title}
                        />
                    )}
                </Container>
                <VisitorsGuideBlogs
                    blogs={allBlogs}
                    isMobileView={isMobileView}
                    destinationId={pageContent.slug}
                    title={`The Latest From ${pageContent.title.split(' ')[0]}`}
                    outline
                />
            </div>
        </Layout>
    );
};

export default AuthorPage;
